import React, { useState, useEffect } from 'react';

interface SupplierDetail {
  id?: number;
  description: string;
  features: string[];
  established_year: string;
  representative: string;
  headquarters_address: string;
  capital: string;
  employees: string;
  business_content: string;
  major_clients: string;
  thumbnail?: File | string | null;
  thumbnail_url?: string | null;
}

interface Supplier {
  id: number;
  name: string;
  slug: string;
  corporate_url: string;
  affiliate_link: string;
  address_raw: string;
  business_type_raw: string;
  supplier_detail?: SupplierDetail;
}

interface SupplierDetailEditorProps {
  supplier: Supplier;
  updateUrl: string;
  updateBasicUrl: string;
  csrfToken: string;
}

const SupplierDetailEditor: React.FC<SupplierDetailEditorProps> = ({
  supplier,
  updateUrl,
  updateBasicUrl,
  csrfToken,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');

  const [basicInfo, setBasicInfo] = useState({
    name: supplier.name,
    slug: supplier.slug,
    corporate_url: supplier.corporate_url,
    affiliate_link: supplier.affiliate_link,
    address_raw: supplier.address_raw,
    business_type_raw: supplier.business_type_raw,
  });

  const [detail, setDetail] = useState<SupplierDetail>({
    description: supplier.supplier_detail?.description || '',
    features: supplier.supplier_detail?.features || [],
    established_year: supplier.supplier_detail?.established_year || '',
    representative: supplier.supplier_detail?.representative || '',
    headquarters_address: supplier.supplier_detail?.headquarters_address || '',
    capital: supplier.supplier_detail?.capital || '',
    employees: supplier.supplier_detail?.employees || '',
    business_content: supplier.supplier_detail?.business_content || '',
    major_clients: supplier.supplier_detail?.major_clients || '',
    thumbnail: null,
  });

  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);

  // コンポーネント初期化時に既存のサムネイル画像を確認
  useEffect(() => {
    if (supplier.supplier_detail?.thumbnail_url) {
      // 既存のサムネイル画像がある場合はプレビュー状態を設定
      setThumbnailPreview(supplier.supplier_detail.thumbnail_url);
    }
  }, [supplier.supplier_detail?.thumbnail_url]);

  const handleInputChange = (field: keyof SupplierDetail, value: string) => {
    setDetail({ ...detail, [field]: value });
  };

  const handleFeatureChange = (index: number, value: string) => {
    const newFeatures = [...detail.features];
    newFeatures[index] = value;
    setDetail({ ...detail, features: newFeatures });
  };

  const addFeature = () => {
    setDetail({ ...detail, features: [...detail.features, ''] });
  };

  const removeFeature = (index: number) => {
    const newFeatures = detail.features.filter((_, i) => i !== index);
    setDetail({ ...detail, features: newFeatures });
  };

  const handleThumbnailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // ファイル形式のバリデーション
      if (!file.type.match(/^image\/(png|jpeg|jpg)$/)) {
        setErrors(['画像ファイルはPNG、JPEG、JPG形式のみ対応しています']);
        return;
      }
      
      // ファイルサイズのバリデーション（5MB）
      if (file.size > 5 * 1024 * 1024) {
        setErrors(['画像ファイルは5MB以下にしてください']);
        return;
      }

      setThumbnailFile(file);
      setErrors([]);
      
      // プレビュー用のURLを生成
      const reader = new FileReader();
      reader.onload = (e) => {
        setThumbnailPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveThumbnail = () => {
    setThumbnailFile(null);
    setThumbnailPreview(null);
  };

  const refreshThumbnailState = async () => {
    try {
      // APIからSupplier情報を取得してサムネイルURLを更新
      const response = await fetch(`/api/v1/admin/suppliers/${supplier.id}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
      });
      
      if (response.ok) {
        const result = await response.json();
        
        if (result.success && result.data.thumbnail_url) {
          // サムネイル画像がある場合はプレビュー状態を更新
          setThumbnailPreview(result.data.thumbnail_url);
        } else {
          // サムネイル画像がない場合はプレビュー状態をクリア
          setThumbnailPreview(null);
        }
      }
    } catch (error) {
      console.error('Failed to refresh thumbnail state:', error);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setErrors([]);
    setSuccessMessage('');

    try {
      // Supplier基本情報を更新
      const basicResponse = await fetch(updateBasicUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          supplier: basicInfo,
        }),
      });

      const basicData = await basicResponse.json();

      if (!basicResponse.ok || !basicData.success) {
        setErrors(basicData.errors || ['基本情報の保存に失敗しました']);
        setIsSaving(false);
        return;
      }

      // SupplierDetailを更新（FormDataを使用してファイルアップロードに対応）
      const formData = new FormData();
      formData.append('supplier_detail[description]', detail.description);
      formData.append('supplier_detail[established_year]', detail.established_year);
      formData.append('supplier_detail[representative]', detail.representative);
      formData.append('supplier_detail[headquarters_address]', detail.headquarters_address);
      formData.append('supplier_detail[capital]', detail.capital);
      formData.append('supplier_detail[employees]', detail.employees);
      formData.append('supplier_detail[business_content]', detail.business_content);
      formData.append('supplier_detail[major_clients]', detail.major_clients);
      
      // featuresを配列として追加
      detail.features.forEach((feature, index) => {
        formData.append(`supplier_detail[features][]`, feature);
      });
      
      // サムネイル画像がある場合は追加
      if (thumbnailFile) {
        formData.append('supplier_detail[thumbnail]', thumbnailFile);
      }

      const detailResponse = await fetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': csrfToken,
        },
        body: formData,
      });

      const detailData = await detailResponse.json();

      if (detailResponse.ok && detailData.success) {
        setSuccessMessage('保存しました');
        setIsEditing(false);
        
        // サムネイル画像の状態をクリア
        setThumbnailFile(null);
        
        // サーバーから最新のサムネイル画像状態を取得
        await refreshThumbnailState();
      } else {
        setErrors(detailData.errors || ['詳細情報の保存に失敗しました']);
      }
    } catch (error) {
      setErrors(['ネットワークエラーが発生しました']);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setBasicInfo({
      name: supplier.name,
      slug: supplier.slug,
      corporate_url: supplier.corporate_url,
      affiliate_link: supplier.affiliate_link,
      address_raw: supplier.address_raw,
      business_type_raw: supplier.business_type_raw,
    });
    setDetail({
      description: supplier.supplier_detail?.description || '',
      features: supplier.supplier_detail?.features || [],
      established_year: supplier.supplier_detail?.established_year || '',
      representative: supplier.supplier_detail?.representative || '',
      headquarters_address: supplier.supplier_detail?.headquarters_address || '',
      capital: supplier.supplier_detail?.capital || '',
      employees: supplier.supplier_detail?.employees || '',
      business_content: supplier.supplier_detail?.business_content || '',
      major_clients: supplier.supplier_detail?.major_clients || '',
      thumbnail: null,
    });
    setThumbnailFile(null);
    setThumbnailPreview(null);
    setIsEditing(false);
    setErrors([]);
  };

  const handleGenerateAllDetails = async () => {
    if (!basicInfo.corporate_url) {
      setErrors(['会社URLが設定されていません。']);
      return;
    }

    if (!confirm('AIで企業情報を自動生成します。SupplierDetail の現在の入力内容は上書きされますがよろしいですか？')) {
      return;
    }

    setIsGenerating(true);
    setErrors([]);
    setSuccessMessage('');

    try {
      const response = await fetch(`/api/v1/admin/suppliers/${supplier.id}/generate_description`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setDetail({
          description: result.data.description || '',
          features: result.data.features || [],
          established_year: result.data.established_year || '',
          representative: result.data.representative || '',
          headquarters_address: result.data.headquarters_address || '',
          capital: result.data.capital || '',
          employees: result.data.employees || '',
          business_content: result.data.business_content || '',
          major_clients: result.data.major_clients || '',
        });
        setSuccessMessage('企業情報を自動生成しました');
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setErrors(result.errors || ['企業情報の生成に失敗しました']);
      }
    } catch (error) {
      setErrors(['ネットワークエラーが発生しました']);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateThumbnail = async () => {
    if (!basicInfo.corporate_url) {
      setErrors(['会社URLが設定されていません。']);
      return;
    }

    if (!confirm('会社URLからサムネイル画像を自動生成します。既存のサムネイルは上書きされますがよろしいですか？')) {
      return;
    }

    setIsGeneratingThumbnail(true);
    setErrors([]);
    setSuccessMessage('');

    try {
      const response = await fetch(`/api/v1/admin/suppliers/${supplier.id}/generate_thumbnail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccessMessage(result.message || 'サムネイル画像の生成を開始しました');
        
        // 現在のサムネイルURLを保存
        const originalThumbnailUrl = thumbnailPreview;
        
        // ポーリングでサムネイルの生成状態をチェック
        let pollCount = 0;
        const maxPolls = 15; // 最大15回（約15秒）
        const pollInterval = setInterval(async () => {
          pollCount++;
          
          try {
            // サムネイル状態を確認
            const checkResponse = await fetch(`/api/v1/admin/suppliers/${supplier.id}`, {
              method: 'GET',
              headers: {
                'Accept': 'application/json',
                'X-CSRF-Token': csrfToken,
              },
            });
            
            if (checkResponse.ok) {
              const checkResult = await checkResponse.json();
              
              // 新しいサムネイルが生成された場合
              if (checkResult.success && checkResult.data.thumbnail_url && 
                  checkResult.data.thumbnail_url !== originalThumbnailUrl) {
                clearInterval(pollInterval);
                setThumbnailPreview(checkResult.data.thumbnail_url);
                setSuccessMessage('サムネイル画像を更新しました');
                setIsGeneratingThumbnail(false);
                return;
              }
            }
            
            // 最大試行回数に達した場合
            if (pollCount >= maxPolls) {
              clearInterval(pollInterval);
              setIsGeneratingThumbnail(false);
              setSuccessMessage('サムネイル画像の生成に時間がかかっています。ページを更新してご確認ください。');
            }
          } catch (error) {
            console.error('Polling error:', error);
          }
        }, 1000); // 1秒ごとにチェック
      } else {
        setErrors(result.errors || ['サムネイル画像の生成に失敗しました']);
        setIsGeneratingThumbnail(false);
      }
    } catch (error) {
      setErrors(['ネットワークエラーが発生しました']);
      setIsGeneratingThumbnail(false);
    }
  };

  return (
    <div className="container-fluid p-0">
      {/* Success Message */}
      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <i className="bi bi-check-circle me-2"></i>
          {successMessage}
          <button type="button" className="btn-close" onClick={() => setSuccessMessage('')}></button>
        </div>
      )}

      {/* Error Messages */}
      {errors.length > 0 && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>エラーが発生しました:</strong>
          <ul className="mb-0 mt-2">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
          <button type="button" className="btn-close" onClick={() => setErrors([])}></button>
        </div>
      )}

      {/* アクションボタン */}
      <div className="d-flex justify-content-end gap-2 mb-3">
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="btn btn-primary"
          >
            <i className="bi bi-pencil me-1"></i>
            編集
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-outline-success"
              onClick={handleGenerateAllDetails}
              disabled={isGenerating || isSaving || isGeneratingThumbnail || !basicInfo.corporate_url}
              title={!basicInfo.corporate_url ? '会社URLを先に設定してください' : 'AIで企業情報を自動生成'}
            >
              {isGenerating ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  AI生成中...
                </>
              ) : (
                <>
                  <i className="bi bi-robot me-1"></i>
                  AI自動生成
                </>
              )}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || isGenerating || isGeneratingThumbnail}
              className="btn btn-primary"
            >
              {isSaving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  保存中...
                </>
              ) : (
                <>
                  <i className="bi bi-check-lg me-1"></i>
                  保存
                </>
              )}
            </button>
            <button
              onClick={handleCancel}
              disabled={isSaving || isGenerating || isGeneratingThumbnail}
              className="btn btn-outline-secondary"
            >
              <i className="bi bi-x-lg me-1"></i>
              キャンセル
            </button>
          </>
        )}
      </div>

      {/* Supplier基本情報（上部） */}
      <div className="card mb-3">
        <div className="card-header bg-primary text-white">
          <h5 className="card-title mb-0">
            <i className="bi bi-building me-2"></i>
            Supplier基本情報
          </h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label fw-bold">会社名</label>
              <input
                type="text"
                className="form-control"
                value={basicInfo.name}
                onChange={(e) => setBasicInfo({ ...basicInfo, name: e.target.value })}
                placeholder="会社名を入力してください"
                disabled={isGenerating || !isEditing}
                style={{ backgroundColor: isEditing ? 'white' : '#f8f9fa' }}
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label fw-bold">スラッグ</label>
              <input
                type="text"
                className="form-control"
                value={basicInfo.slug}
                onChange={(e) => setBasicInfo({ ...basicInfo, slug: e.target.value })}
                placeholder="スラッグを入力してください"
                disabled={isGenerating || !isEditing}
                style={{ backgroundColor: isEditing ? 'white' : '#f8f9fa' }}
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label fw-bold">会社URL</label>
              <input
                type="url"
                className="form-control"
                value={basicInfo.corporate_url}
                onChange={(e) => setBasicInfo({ ...basicInfo, corporate_url: e.target.value })}
                placeholder="https://example.com"
                disabled={isGenerating || !isEditing}
                style={{ backgroundColor: isEditing ? 'white' : '#f8f9fa' }}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">アフィリエイトリンク</label>
              <input
                type="url"
                className="form-control"
                value={basicInfo.affiliate_link}
                onChange={(e) => setBasicInfo({ ...basicInfo, affiliate_link: e.target.value })}
                placeholder="https://example.com/affiliate/..."
                disabled={isGenerating || !isEditing}
                style={{ backgroundColor: isEditing ? 'white' : '#f8f9fa' }}
              />
              <div className="form-text">
                設定すると、フロントエンドで優先的にこのリンクが使用されます
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">住所（Raw）</label>
              <textarea
                className="form-control"
                rows={2}
                value={basicInfo.address_raw}
                onChange={(e) => setBasicInfo({ ...basicInfo, address_raw: e.target.value })}
                placeholder="住所を入力してください"
                disabled={isGenerating || !isEditing}
                style={{ backgroundColor: isEditing ? 'white' : '#f8f9fa' }}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">ビジネスタイプ（Raw）</label>
              <textarea
                className="form-control"
                rows={2}
                value={basicInfo.business_type_raw}
                onChange={(e) => setBasicInfo({ ...basicInfo, business_type_raw: e.target.value })}
                placeholder="ビジネスタイプを入力してください"
                disabled={isGenerating || !isEditing}
                style={{ backgroundColor: isEditing ? 'white' : '#f8f9fa' }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* SupplierDetail詳細情報（下部） */}
      <div className="card">
        <div className="card-header bg-info text-white">
          <h5 className="card-title mb-0">
            <i className="bi bi-file-text me-2"></i>
            SupplierDetail詳細情報
          </h5>
        </div>
        <div className="card-body">
          <div className="row">
            {/* 左カラム: テキスト情報 */}
            <div className="col-lg-6">
              {/* Description */}
              <div className="mb-3">
                <label className="form-label fw-bold">会社の説明</label>
                <textarea
                  className="form-control"
                  rows={5}
                  value={detail.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="会社の説明を入力してください"
                  disabled={isGenerating || !isEditing}
                  style={{ backgroundColor: isEditing ? 'white' : '#f8f9fa' }}
                />
              </div>

              {/* Features */}
              <div className="mb-3">
                <label className="form-label fw-bold">特徴</label>
                {detail.features.map((feature, index) => (
                  <div key={index} className="d-flex gap-2 mb-2">
                    <input
                      type="text"
                      className="form-control"
                      value={feature}
                      onChange={(e) => handleFeatureChange(index, e.target.value)}
                      placeholder="特徴を入力してください"
                      disabled={isGenerating || !isEditing}
                      style={{ backgroundColor: isEditing ? 'white' : '#f8f9fa' }}
                    />
                    {isEditing && (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => removeFeature(index)}
                        disabled={isGenerating}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    )}
                  </div>
                ))}
                {isEditing && (
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm mt-2"
                    onClick={addFeature}
                    disabled={isGenerating}
                  >
                    <i className="bi bi-plus-lg me-1"></i>
                    特徴を追加
                  </button>
                )}
              </div>

              {/* Business Content */}
              <div className="mb-3">
                <label className="form-label fw-bold">事業内容</label>
                <textarea
                  className="form-control"
                  rows={5}
                  value={detail.business_content}
                  onChange={(e) => handleInputChange('business_content', e.target.value)}
                  placeholder="事業内容を入力してください"
                  disabled={isGenerating || !isEditing}
                  style={{ backgroundColor: isEditing ? 'white' : '#f8f9fa' }}
                />
              </div>

              {/* Major Clients */}
              <div className="mb-3">
                <label className="form-label fw-bold">主要取引先</label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={detail.major_clients}
                  onChange={(e) => handleInputChange('major_clients', e.target.value)}
                  placeholder="主要取引先を入力してください"
                  disabled={isGenerating || !isEditing}
                  style={{ backgroundColor: isEditing ? 'white' : '#f8f9fa' }}
                />
              </div>
            </div>

            {/* 右カラム: 会社基本情報 */}
            <div className="col-lg-6">
              {/* Established Year */}
              <div className="mb-3">
                <label className="form-label fw-bold">設立年</label>
                <input
                  type="text"
                  className="form-control"
                  value={detail.established_year}
                  onChange={(e) => handleInputChange('established_year', e.target.value)}
                  placeholder="例: 1997年6月"
                  disabled={isGenerating || !isEditing}
                  style={{ backgroundColor: isEditing ? 'white' : '#f8f9fa' }}
                />
              </div>

              {/* Representative */}
              <div className="mb-3">
                <label className="form-label fw-bold">代表者</label>
                <input
                  type="text"
                  className="form-control"
                  value={detail.representative}
                  onChange={(e) => handleInputChange('representative', e.target.value)}
                  placeholder="代表者名を入力してください"
                  disabled={isGenerating || !isEditing}
                  style={{ backgroundColor: isEditing ? 'white' : '#f8f9fa' }}
                />
              </div>

              {/* Capital */}
              <div className="mb-3">
                <label className="form-label fw-bold">資本金</label>
                <input
                  type="text"
                  className="form-control"
                  value={detail.capital}
                  onChange={(e) => handleInputChange('capital', e.target.value)}
                  placeholder="例: 1億円"
                  disabled={isGenerating || !isEditing}
                  style={{ backgroundColor: isEditing ? 'white' : '#f8f9fa' }}
                />
              </div>

              {/* Employees */}
              <div className="mb-3">
                <label className="form-label fw-bold">社員数</label>
                <input
                  type="text"
                  className="form-control"
                  value={detail.employees}
                  onChange={(e) => handleInputChange('employees', e.target.value)}
                  placeholder="例: 250名"
                  disabled={isGenerating || !isEditing}
                  style={{ backgroundColor: isEditing ? 'white' : '#f8f9fa' }}
                />
              </div>

              {/* Headquarters Address */}
              <div className="mb-3">
                <label className="form-label fw-bold">本社所在地</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={detail.headquarters_address}
                  onChange={(e) => handleInputChange('headquarters_address', e.target.value)}
                  placeholder="本社の住所を入力してください"
                  disabled={isGenerating || !isEditing}
                  style={{ backgroundColor: isEditing ? 'white' : '#f8f9fa' }}
                />
              </div>

              {/* Thumbnail Upload */}
              <div className="mb-3">
                <label className="form-label fw-bold">サムネイル画像</label>
                
                {/* サムネイル生成中のローディング表示 */}
                {isGeneratingThumbnail && (
                  <div className="mb-3 text-center border rounded p-4" style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa' }}>
                    <div>
                      <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">生成中...</span>
                      </div>
                      <div className="fw-bold text-muted">
                        <i className="bi bi-camera me-2"></i>
                        スクリーンショットを生成中です...
                      </div>
                      <div className="text-muted small mt-2">
                        完了までしばらくお待ちください（約10秒）
                      </div>
                    </div>
                  </div>
                )}
                
                {/* サムネイルプレビュー（生成中でない場合のみ表示） */}
                {!isGeneratingThumbnail && thumbnailPreview && (
                  <div className="mb-3">
                    <img
                      src={thumbnailPreview}
                      alt="Thumbnail preview"
                      className="img-thumbnail"
                      style={{ maxWidth: '200px', maxHeight: '200px' }}
                    />
                    {isEditing && (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm ms-2"
                        onClick={handleRemoveThumbnail}
                        disabled={isGenerating}
                      >
                        <i className="bi bi-trash"></i> 削除
                      </button>
                    )}
                  </div>
                )}
                
                {/* サムネイルなし＆生成中でない場合のプレースホルダー */}
                {!isGeneratingThumbnail && !thumbnailPreview && (
                  <div className="mb-3 text-center border rounded p-4 text-muted" style={{ minHeight: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa' }}>
                    <div>
                      <i className="bi bi-image" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
                      <div className="mt-2 small">サムネイル画像が設定されていません</div>
                    </div>
                  </div>
                )}
                
                {isEditing && (
                  <>
                    <input
                      type="file"
                      className="form-control mb-2"
                      accept="image/png,image/jpeg,image/jpg"
                      onChange={handleThumbnailChange}
                      disabled={isGenerating || isGeneratingThumbnail}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={handleGenerateThumbnail}
                      disabled={isGenerating || isGeneratingThumbnail || !basicInfo.corporate_url}
                      title={!basicInfo.corporate_url ? '会社URLを先に設定してください' : '会社URLから自動取得'}
                    >
                      {isGeneratingThumbnail ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          自動取得中...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-camera me-1"></i>
                          サムネイル自動取得
                        </>
                      )}
                    </button>
                  </>
                )}
                <div className="form-text">
                  PNG、JPEG、JPG形式、5MB以下の画像ファイルをアップロードできます。<br />
                  または「サムネイル自動取得」ボタンで会社URLからスクリーンショットを自動生成できます。
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierDetailEditor;
